/**
 * THE MAIN MENU, AS DATA — one screen, for every role that works a building.
 *
 * ── WHAT THIS REPLACED ──
 * The menu used to be an ORGANISATION CHART of the product: a "Going in" group,
 * a "Coming out" group, a Reports group, a tail of tools, and a submit button at
 * the foot of the page that only became pressable after you pressed a tile which
 * navigated nowhere. Three headings and two lines of hint text before the first
 * thing anyone wanted to do.
 *
 * ── ONE SUBJECT, ONE CARD, TWO DIRECTIONS ──
 * Three kinds of thing come in and go out — a visitor, a resident, a parcel —
 * and that IS the job. Splitting each into two separate buttons made ten
 * look-alike controls out of five subjects, so the desk read the whole list to
 * find one of them. They are now three cards that each name their subject once
 * and carry In and Out inside:
 *
 *     Visitor              [ In ][ Out ]
 *     Resident             [ In ][ Out ]
 *     Parcel               [ In ][ Out ]
 *     Building Activities  [ In ][ Out ]
 *     Expected · Who's home · Shift Monitor
 *
 * "Building Activities" is what the screen behind it calls ITSELF (daily-log.tsx
 * renders that heading over Maintenance / Leasing office / Resident /
 * Miscellaneous / Shift change). A button and the page it opens must agree, or
 * the desk presses "Write in the log" and lands somewhere with another name on
 * it. The DATABASE still calls this table `daily_log` and always will; the
 * product has called it Building Activities since that screen was written, and
 * this menu follows the product.
 *
 * ── WHAT LEFT, AND WHY ──
 * READ THE LOG is gone. It was a fifth way to look at the same rows: the
 * activity feed under this grid already shows everything in and out at this
 * desk, filterable and searchable, without leaving the menu. Two buttons onto
 * one dataset is how a desk learns to distrust both.
 *
 * HANDOVER is gone from the grid, and nothing is lost with it — the handover
 * is not something you go and fetch, it is what clocking out produces and what
 * clocking in hands you. ShiftClock already raises the record in both
 * directions (it opens the receipt on clock-out and `started.claimed_handoff`
 * on clock-in), so the tile was a third door onto a record the shift already
 * delivers twice. Owner instruction, 2026-08-13: "we clock out and handover".
 *
 * You pick WHAT first and WHICH WAY second, which is the order the desk already
 * thinks in — somebody is standing there, and they are either arriving or
 * leaving.
 *
 * BOTH HALVES MUST DO SOMETHING DIFFERENT, or a pair is just a decorated single
 * button. Visitor In opens the arrival wizard and Out opens the exit desk;
 * Parcel In opens intake and Out opens handover; Resident In lists everybody
 * while Out lists only the residents currently signed in, so the desk is not
 * hunting for one name in the whole building.
 *
 * ── WHAT IS NOT HERE ──
 * The shift clock and the handover are not errands: one is the state of your own
 * shift and the other is something to read. They live in the title column
 * (main-menu.tsx).
 *
 * ── THE RULES THIS MODULE STILL ENFORCES ──
 * 1. EVERY OPTION KEEPS ITS PERMISSION GATE, as data, asserted per option — a
 *    restructure that quietly widens access is a security regression. An item
 *    whose options are ALL hidden disappears with them.
 * 2. A HIDDEN OPTION HAS NO DESTINATION. navigationTargets is derived from what
 *    was actually built for THIS user.
 */

/**
 * THE "SEND THE REPORT" ID, KEPT THOUGH THE TILE IS GONE.
 *
 * Owner instruction, 2026-08-13: "we don't need send the report because we have
 * clock out and hand over — that should send a report of everything that
 * happened on this shift to the concierges, admins and managers."
 *
 * Which is the better design: a report of a shift is produced BY the shift
 * ending, not by somebody remembering to press a button afterwards, and a
 * button that can be forgotten produces a record with holes in it. The send now
 * happens server-side when the handover is written — see the handoff
 * notification in lobbero_be/src/CRUD/shift-handoff.js.
 *
 * The constant survives because main-menu.tsx still branches on it to
 * distinguish an ACTION from a navigation, and because a future surface may
 * want an explicit re-send. It names nothing on this menu today.
 */
export const SEND_REPORT_OPTION = '/send-report';

/**
 * The standalone "sign a visitor out" screen.
 *
 * NOT '/checkout': this app already talks about a Stripe checkout
 * (POST /subscriptions/checkout), and one word meaning both "a visitor is
 * leaving the building" and "an owner is paying us" is a reading error waiting
 * to happen in a router table.
 */
export const VISITOR_EXIT_PATH = '/visitor-exit';

/**
 * ONE parcel screen, TWO doors into it. Intake and handover are two halves of
 * one shelf and one source of truth (`parcel-desk.tsx`); a deep link costs a
 * query parameter and keeps them single.
 */
export const PARCEL_INTAKE_PATH = '/parcels?section=intake';
export const PARCEL_HANDOVER_PATH = '/parcels?section=handover';

/**
 * Resident in / out — one screen, no wizard, and it reads `?direction=` so the
 * OUT half opens on the people who are actually inside.
 */
export const RESIDENT_ENTRY_PATH = '/visitor-log/resident';
export const RESIDENT_IN_PATH = `${RESIDENT_ENTRY_PATH}?direction=in`;
export const RESIDENT_OUT_PATH = `${RESIDENT_ENTRY_PATH}?direction=out`;

/**
 * Building Activities in / out.
 *
 * IN is the wizard that was already there — the whole daily-log screen,
 * unchanged. OUT is a separate, simpler screen in the shape every other Out on
 * this menu takes: a list of what is still open, one tap to close it.
 *
 * They are two different screens rather than one deep link because the two acts
 * are not symmetric. Writing a building activity is a five-step wizard with a
 * category, a priority and free text; closing one is picking a row. Pointing
 * both halves at `/daily-log` would have made the pair decoration, which this
 * module's own rule forbids.
 */
export const DAILY_LOG_PATH = '/daily-log';
export const DAILY_LOG_EXIT_PATH = '/daily-log-exit';

/**
 * Expected today — the visitor passes due at this desk today (resident-portal
 * Plan 2, RP-2.11). A standalone route under the /visitor-log prefix, so it is a
 * desk screen with building context, gated on `visitorpass:read`.
 */
export const EXPECTED_PATH = '/visitor-log/expected';

export type MenuIcon = 'visitor' | 'daily' | 'check' | 'scan' | 'parcel';

/**
 * One pressable thing.
 *
 *   navigate  go to `to` (almost everything).
 *   action    run an in-page handler — today only "Send the report", which
 *             sends it. The old menu made you press a tile that changed nothing
 *             visible and then a button at the bottom of the page.
 */
export interface MenuOption {
  /** Also the selected-option key, and the navigation-target key. */
  id: string;
  /** 'In' / 'Out' inside a pair; absent on a single-option card. */
  label?: string;
  kind: 'navigate' | 'action';
  to?: string;
}

/**
 * One card. `options` has ONE entry (the whole card is the button) or TWO (the
 * card names the subject and carries In and Out).
 */
export interface MenuItem {
  id: string;
  label: string;
  icon: MenuIcon;
  options: MenuOption[];
  /**
   * Dead without a chosen building. Everything that reads or writes
   * building-scoped data is.
   */
  requiresBuilding: boolean;
  /**
   * A count drawn on the card (CountBadge, brand — never alert). Absent or 0 =
   * no badge. Today only the Expected card carries one: the visitors signable
   * now whom nobody has signed in yet.
   */
  badge?: number;
  /**
   * The card's accessible name when it differs from `label` — a badge is
   * aria-hidden, so its number has to be READ with the label ("Expected, 3
   * today") or a screen reader hears only "Expected".
   */
  accessibleName?: string;
  /**
   * THIS RECORDS SOMETHING THAT HAPPENED ON A SHIFT.
   *
   * A visitor arriving, a resident going out, a parcel taken in, a note in the
   * building log — every one of them is an event attributed to whoever was on
   * duty, and "on duty" is the clock. So these wait for it: the desk clocks in
   * first, and the menu says so instead of accepting entries against a shift
   * that was never opened.
   *
   * READING IS NOT WORKING. Who's home, the log, the monitor and the handover
   * stay available off the clock — a manager checking a building at midnight is
   * not on that building's desk, and locking them out of a read would be a new
   * problem in place of the one this solves.
   */
  recordsWork?: boolean;
}

/**
 * What this user may do — the same permissions the endpoints check, passed in
 * rather than read here so the model stays pure.
 */
export interface MenuCapabilities {
  /** PACKAGE_READ — both parcel doors. */
  canSeePackages: boolean;
  /**
   * VISITORLOG_CREATE — recording that somebody left is the same class of act
   * as recording that they arrived, and POST /visitor-log/:id/exit is gated on
   * precisely this.
   */
  canSignVisitorsOut: boolean;
  /** OCCUPANCY_READ — the roster, and therefore Resident in/out too. */
  canSeeRoster: boolean;
  /**
   * USER_READ. Deliberately NOT shift:clock: the monitor reports that a named
   * person is logged in and has not clocked in, which is supervision.
   */
  canSeeShiftMonitor: boolean;
  /**
   * REPORT_EXPORT — sending the day out.
   *
   * There is no canReadReport any more, and no canReadHandover. Both gated
   * buttons this menu no longer has; see the "WHAT LEFT" note at the top of
   * this file. The page still computes REPORT_READ for the activity feed under
   * the grid — that gate lives at the feed, not here.
   */
  canSendReport: boolean;
  /**
   * VISITORPASS_READ AND the building accepts passes — the Expected card.
   * main-menu.tsx also withdraws it the moment the live /expected answer says
   * passes are switched off (see expectedCardVisible).
   */
  canSeeExpected: boolean;
  /**
   * Lobbero Stays (plan S3): STAYS_DESK AND the building has Stays on, and the
   * live lane has not since said it is off (expected-stays-model.ts
   * expectedStaysCardVisible). The Expected card shows when EITHER this or
   * canSeeExpected holds — a building with passes off and Stays on still has
   * guests to expect. Optional, so a caller that knows nothing of Stays reads
   * as off.
   */
  canSeeExpectedStays?: boolean;
}

/** Live numbers the cards carry. Kept apart from the capabilities: those say
 *  what may be SHOWN, these say what is on it. */
export interface MenuCounts {
  /** Visitors signable now whom nobody has signed in yet — the pass visitors
   *  plus the short-stay guests the desk can sign in now (expectedBadgeCount). */
  expected?: number;
}

const when = <T,>(visible: boolean, value: T): T[] => (visible ? [value] : []);

/** Drop any card left with no options at all by the gates above. */
const live = (items: MenuItem[]): MenuItem[] =>
  items.filter(item => item.options.length > 0);

/**
 * THE MENU, in the order a shift happens: the three subjects, then the log,
 * then the boards.
 */
export const buildMenuItems = (
  caps: MenuCapabilities,
  counts: MenuCounts = {}
): MenuItem[] =>
  live([
    {
      id: 'visitor',
      label: 'Visitor',
      icon: 'visitor',
      requiresBuilding: true,
      recordsWork: true,
      options: [
        { id: '/visitor-log', label: 'In', kind: 'navigate', to: '/visitor-log' },
        ...when(caps.canSignVisitorsOut, {
          id: VISITOR_EXIT_PATH,
          label: 'Out',
          kind: 'navigate' as const,
          to: VISITOR_EXIT_PATH,
        }),
      ],
    },
    {
      // THE VISITORS WHO ARE COMING. Straight after Visitor, because it is the
      // same subject seen from the other side: who is due, and the one tap that
      // signs them in on their pass. ONE option, so it renders as a board.
      //
      // recordsWork: false. Reading the list is not work — the roster and the
      // shift monitor are not recordsWork either. Signing somebody in IS, and
      // the screen shift-gates those actions itself.
      id: EXPECTED_PATH,
      label: 'Expected',
      icon: 'visitor',
      requiresBuilding: true,
      recordsWork: false,
      ...(counts.expected && counts.expected > 0
        ? {
            badge: counts.expected,
            accessibleName: `Expected, ${counts.expected} today`,
          }
        : {}),
      // Passes OR short stays: one card for everybody the desk expects.
      options: when(caps.canSeeExpected || !!caps.canSeeExpectedStays, {
        id: EXPECTED_PATH,
        kind: 'navigate' as const,
        to: EXPECTED_PATH,
      }),
    },
    {
      id: 'resident',
      label: 'Resident',
      icon: 'check',
      requiresBuilding: true,
      recordsWork: true,
      // Gated on the roster, because that is literally what the screen loads.
      options: caps.canSeeRoster
        ? [
            { id: RESIDENT_IN_PATH, label: 'In', kind: 'navigate', to: RESIDENT_IN_PATH },
            { id: RESIDENT_OUT_PATH, label: 'Out', kind: 'navigate', to: RESIDENT_OUT_PATH },
          ]
        : [],
    },
    {
      id: 'parcel',
      label: 'Parcel',
      icon: 'parcel',
      requiresBuilding: true,
      recordsWork: true,
      options: caps.canSeePackages
        ? [
            { id: PARCEL_INTAKE_PATH, label: 'In', kind: 'navigate', to: PARCEL_INTAKE_PATH },
            {
              id: PARCEL_HANDOVER_PATH,
              label: 'Out',
              kind: 'navigate',
              to: PARCEL_HANDOVER_PATH,
            },
          ]
        : [],
    },
    {
      // THE FOURTH PAIR. A building activity has two sides like the other three
      // subjects: a contractor, a crew or an office visit starts, and later it
      // ends. Only the first half was ever pressable, so "the maintenance crew
      // left at six" had nowhere to go and the desk wrote a second note about
      // the end of the first one.
      id: 'activity',
      label: 'Building Activities',
      icon: 'daily',
      requiresBuilding: true,
      recordsWork: true,
      options: [
        { id: DAILY_LOG_PATH, label: 'In', kind: 'navigate', to: DAILY_LOG_PATH },
        {
          id: DAILY_LOG_EXIT_PATH,
          label: 'Out',
          kind: 'navigate',
          to: DAILY_LOG_EXIT_PATH,
        },
      ],
    },
    {
      id: '/roster',
      label: "Who's home",
      icon: 'visitor',
      requiresBuilding: true,
      options: when(caps.canSeeRoster, {
        id: '/roster',
        kind: 'navigate' as const,
        to: '/roster',
      }),
    },
    {
      id: '/shift-monitor',
      label: 'Shift Monitor',
      icon: 'check',
      requiresBuilding: true,
      options: when(caps.canSeeShiftMonitor, {
        id: '/shift-monitor',
        kind: 'navigate' as const,
        to: '/shift-monitor',
      }),
    },
  ]);

/**
 * The navigation whitelist, derived from what was actually rendered — so an
 * option the user cannot see has no destination at all.
 */
export const navigationTargets = (items: MenuItem[]): Record<string, string> => {
  const targets: Record<string, string> = {};
  items.forEach(item =>
    item.options.forEach(option => {
      if (option.kind === 'navigate' && option.to) targets[option.id] = option.to;
    })
  );

  return targets;
};
